import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Chip,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Button,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { uploadFiles, checkPasswordProtectionAI as checkPasswordProtection, verifyPasswordAI as verifyPassword } from '../../api/upload';
import SoftwareAgent from './software-agent.png';
import {
  AttachFile,
  Send,
  CheckCircle,
  Close,
  InsertDriveFile,
  LockOutlined,
} from '@mui/icons-material';
import gesLogo from './ges-logos.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B3B58',
      light: '#81B3C0',
      dark: '#1B3B58',
    },
    secondary: {
      main: '#DD7C25',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
  },
});

const FileUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Password protection states
  const [filePasswords, setFilePasswords] = useState({});
  const [passwordDialogs, setPasswordDialogs] = useState({});
  const [passwordInputs, setPasswordInputs] = useState({});
  const [passwordVerifying, setPasswordVerifying] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => file.name.endsWith('.xlsx'));

    if (validFiles.length !== files.length) {
      setSnackbar({
        open: true,
        message: 'Only Excel files with the .xlsx extension are supported',
        severity: 'warning',
      });
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      
      // Check each file for password protection
      for (const file of validFiles) {
        try {
          const result = await checkPasswordProtection(file);
          if (result.is_password_protected) {
            setPasswordDialogs(prev => ({ ...prev, [file.name]: true }));
            setSnackbar({
              open: true,
              message: `${file.name} is password protected. Please enter the password.`,
              severity: 'info',
            });
          }
        } catch (error) {
          console.error(`Failed to check password for ${file.name}:`, error);
        }
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    const fileToRemove = selectedFiles[index];
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Clean up password-related states for removed file
    if (fileToRemove) {
      setPasswordDialogs(prev => {
        const newState = { ...prev };
        delete newState[fileToRemove.name];
        return newState;
      });
      setFilePasswords(prev => {
        const newState = { ...prev };
        delete newState[fileToRemove.name];
        return newState;
      });
      setPasswordInputs(prev => {
        const newState = { ...prev };
        delete newState[fileToRemove.name];
        return newState;
      });
      setPasswordVerifying(prev => {
        const newState = { ...prev };
        delete newState[fileToRemove.name];
        return newState;
      });
      setShowPasswords(prev => {
        const newState = { ...prev };
        delete newState[fileToRemove.name];
        return newState;
      });
    }
  };

  const handlePasswordChange = (filename, value) => {
    setPasswordInputs(prev => ({ ...prev, [filename]: value }));
  };

  const handlePasswordSubmit = async (filename) => {
    const password = passwordInputs[filename];
    if (!password) {
      setSnackbar({
        open: true,
        message: 'Please enter a password',
        severity: 'warning',
      });
      return;
    }

    setPasswordVerifying(prev => ({ ...prev, [filename]: true }));

    try {
      const file = selectedFiles.find(f => f.name === filename);
      const result = await verifyPassword(file, password);

      if (result.password_correct) {
        setFilePasswords(prev => ({ ...prev, [filename]: password }));
        setPasswordDialogs(prev => ({ ...prev, [filename]: false }));
        setSnackbar({
          open: true,
          message: `Password verified successfully for ${filename}`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Incorrect password. Please try again.',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Password verification failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setPasswordVerifying(prev => ({ ...prev, [filename]: false }));
    }
  };

  const isSubmitEnabled = () => {
    if (selectedFiles.length === 0 || uploading) return false;
    
    // Check if all password-protected files have passwords verified
    for (const file of selectedFiles) {
      if (passwordDialogs[file.name] && !filePasswords[file.name]) {
        return false;
      }
    }
    return true;
  };

  const handleUpload = async () => {
  if (!isSubmitEnabled()) {
    setSnackbar({ 
      open: true, 
      message: 'Please verify passwords for all protected files before submitting', 
      severity: 'warning' 
    });
    return;
  }

  setUploading(true);

  try {
    // Upload files and get job IDs
    const response = await uploadFiles(selectedFiles, filePasswords);
    
    const jobs = response.jobs || [];
    
    if (jobs.length === 0) {
      setSnackbar({
        open: true,
        message: 'No jobs were created',
        severity: 'error',
      });
      setUploading(false);
      return;
    }

    // Redirect to the first job's status page
    const firstJobId = jobs[0].job_id;
    localStorage.setItem(`job_filename_${firstJobId}`, selectedFiles[0].name);
    navigate(`/jobs/${firstJobId}`);

  } catch (error) {
    console.error('Upload error:', error);

    let errorMessage;
    if (error.message.includes('timeout')) {
      errorMessage = 'Upload timeout - please check your connection and try again';
    } else if (error.message.includes('Network')) {
      errorMessage = 'Network error - please check your connection';
    } else {
      errorMessage = error.message || 'Upload failed. Please try again.';
    }

    setSnackbar({
      open: true,
      message: errorMessage,
      severity: 'error',
    });
    setUploading(false);
  }
};


  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

        {/* Top-left GES logo */}
        <Box
          component="img"
          src={gesLogo}
          alt="GES Logo"
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            height: 60,
            width: 'auto',
            objectFit: 'contain',
            zIndex: 10,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {/* Welcome Card */}
            <Paper
              elevation={0}
              sx={{
                p: 5,
                mb: 4,
                borderRadius: 4,
                background: 'rgba(255, 248, 248, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow:
                  '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow:
                    '0 30px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.5) inset',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 3,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'float 3s ease-in-out infinite',
                    '@keyframes float': {
                      '0%, 100%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' },
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={SoftwareAgent}
                    alt="Software Agent"
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'contain',
                      borderRadius: 2,
                      filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    mb: 1.5,
                    color: '#1B3B58',
                    fontWeight: 800,
                  }}
                >
                  Welcome to GES Agentic File Upload Assistant
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#1B3B58',
                    fontSize: '1rem',
                    maxWidth: 600,
                    mx: 'auto',
                    animation: 'blink 1.5s ease-in-out infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                    },
                  }}
                >
                  Upload your Excel files and let our intelligent preprocessor agent
                  analyze and optimize your data automatically
                </Typography>

                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Link
                    to="/"
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography
                      sx={{
                        color: '#1B3B58',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      ← Back to Home
                    </Typography>
                  </Link>
                  {/* <Typography sx={{ color: '#cbd5e1' }}>|</Typography> */}
                  <Link
                    to="/jobs"
                    style={{ textDecoration: 'none' }}
                  >
                    {/* <Typography
                      sx={{
                        color: '#DD7C25',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View Job History →
                    </Typography> */}
                  </Link>
                </Box>
              </Box>

              <Divider sx={{ mb: 4, borderColor: 'rgba(0,0,0,0.06)' }} />

              {/* Input Section */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter your prompt or instructions for file processing..."
                multiline
                rows={3}
                disabled={uploading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    background: '#f8fafc',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: '#ffffff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                    '&.Mui-focused': {
                      background: '#ffffff',
                      boxShadow: '0 0 0 3px rgba(27, 59, 88, 0.1)',
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', ml: 1 }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls"
                        multiple
                        style={{ display: 'none' }}
                      />
                      <Tooltip title="Attach Excel files" arrow>
                        <span>
                          <IconButton
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            sx={{
                              background: 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                              color: 'white',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #B45B2C 0%, #8C2F1D 100%)',
                                transform: 'scale(1.05)',
                              },
                              '&:disabled': {
                                background: '#E1E1E1',
                                color: '#8A8550',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <AttachFile />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip 
                        title={
                          !isSubmitEnabled() && selectedFiles.length > 0
                            ? "Please verify all file passwords first"
                            : "Process files"
                        } 
                        arrow
                      >
                        <span>
                          <IconButton
                            onClick={handleUpload}
                            disabled={!isSubmitEnabled()}
                            sx={{
                              background: 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                              color: 'white',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #B45B2C 0%, #8C2F1D 100%)',
                                transform: 'translateX(3px)',
                              },
                              '&:disabled': {
                                background: '#E1E1E1',
                                color: '#8A8550',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Send />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ),
                }}
              />

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: '#334155',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Selected Files ({selectedFiles.length})
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {selectedFiles.map((file, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          mb: 1,
                          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            borderColor: '#DD7C25',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              background: passwordDialogs[file.name] && !filePasswords[file.name]
                                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                                : filePasswords[file.name]
                                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {passwordDialogs[file.name] && !filePasswords[file.name] ? (
                              <LockOutlined sx={{ color: 'white', fontSize: 20 }} />
                            ) : filePasswords[file.name] ? (
                              <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                            ) : (
                              <InsertDriveFile sx={{ color: 'white', fontSize: 20 }} />
                            )}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{formatFileSize(file.size)}</span>
                              {passwordDialogs[file.name] && !filePasswords[file.name] && (
                                <Chip 
                                  label="Password Required" 
                                  size="small" 
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 20,
                                    background: '#fbbf24',
                                    color: 'white',
                                    fontWeight: 600,
                                  }} 
                                />
                              )}
                              {filePasswords[file.name] && (
                                <Chip 
                                  label="Password Verified" 
                                  size="small" 
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    height: 20,
                                    background: '#10b981',
                                    color: 'white',
                                    fontWeight: 600,
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            noWrap: true,
                            color: '#1e293b',
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.8rem',
                            color: '#64748b',
                          }}
                        />
                        <IconButton
                          onClick={() => removeFile(index)}
                          size="small"
                          disabled={uploading}
                          sx={{
                            color: '#ef4444',
                            '&:hover': {
                              background: 'rgba(239, 68, 68, 0.1)',
                              transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Close />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Password Protection Dialogs */}
              {selectedFiles.map((file) => (
                passwordDialogs[file.name] && !filePasswords[file.name] && (
                  <Box
                    key={file.name}
                    sx={{
                      mb: 3,
                      p: 3,
                      background: 'linear-gradient(135deg, #fff8dc 0%, #ffe4b5 100%)',
                      borderRadius: 3,
                      border: '2px solid #DD7C25',
                      boxShadow: '0 4px 12px rgba(221, 124, 37, 0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <LockOutlined sx={{ color: '#DD7C25', fontSize: 24 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1B3B58' }}>
                        Password Required: {file.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                      This file is password protected. Please enter the password to proceed.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        type={showPasswords[file.name] ? "text" : "password"}
                        size="small"
                        placeholder="Enter password"
                        value={passwordInputs[file.name] || ''}
                        onChange={(e) => handlePasswordChange(file.name, e.target.value)}
                        disabled={passwordVerifying[file.name]}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handlePasswordSubmit(file.name);
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: '#ffffff',
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => setShowPasswords(prev => ({ 
                                ...prev, 
                                [file.name]: !prev[file.name] 
                              }))}
                              edge="end"
                              size="small"
                            >
                              {showPasswords[file.name] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          )
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handlePasswordSubmit(file.name)}
                        disabled={passwordVerifying[file.name] || !passwordInputs[file.name]}
                        sx={{
                          minWidth: 120,
                          background: 'linear-gradient(135deg, #DD7C25 0%, #B45B2C 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #B45B2C 0%, #8C2F1D 100%)',
                          },
                          '&:disabled': {
                            background: '#E1E1E1',
                            color: '#8A8550',
                          },
                        }}
                      >
                        {passwordVerifying[file.name] ? 'Verifying...' : 'Verify'}
                      </Button>
                    </Box>
                  </Box>
                )
              ))}
            </Paper>
          </Box>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            elevation={6}
            sx={{ borderRadius: 3, fontWeight: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default FileUpload;
